/**
 * Host (TV) view for an active Scotland Yard game.
 *
 * Layout: full-bleed `BoardCanvas` on the left, fixed-width `GameSidebar` on
 * the right. This is the "TV" mode that App.tsx already exercises in dev —
 * we extract it here so the platform layer can render it whenever
 * `room.status === 'active'` without dragging in dev controls or layout.
 *
 * No interactive controls: this view is read-only. Selection / inspection /
 * "animate move" toys live in the dev harness (App.tsx), not in production
 * gameplay UI.
 */

import { Group } from '@mantine/core'
import type { Room, GameState } from '@mdev/common'
import { BoardCanvas } from './BoardCanvas'
import { GameSidebar } from './GameSidebar'
import { RevealBanner } from './RevealBanner'
import { SY } from '../theme'
import type { ScotlandYardSettings } from '../module'

interface Props {
  room: Room<ScotlandYardSettings>
  state: { public: GameState; mrxStation: number }
}

export function HostGameView( props: Props ) {
  const { state } = props
  // `props.room` is reserved for future use (e.g. game-code header overlay)
  // but the current TV layout pulls all info from `state`.
  // The board renders the public state only — Mr. X's true station never
  // touches the host canvas. The reveal marker (drawn from
  // state.public.mrx.lastRevealedStation) is the only Mr. X position the
  // TV is ever allowed to show.
  return (
    <Group
      gap={ 0 }
      wrap="nowrap"
      style={ { width: '100%', height: '100%', minHeight: 0, background: SY.pageBg, position: 'relative' } }
    >
      <div style={ { flex: 1, minWidth: 0, height: '100%' } }>
        <BoardCanvas
          options={ { showMap: true, showEdges: false, showStations: true, dimMap: true } }
          selectedId={ null }
          onSelect={ () => { /* no-op: gameplay UI is non-interactive on the host */ } }
          gameState={ state.public }
        />
      </div>
      <GameSidebar state={ state.public } />
      <RevealBanner state={ state.public } />
    </Group>
  )
}
